import { useEffect, useMemo } from 'react';

import { mergeBVHFiles, mergeBVHFilesAtTimelineOffsets } from '@cartwhl/3d/bvh';

import { resolvedSequenceMatchesRequestedSource } from '../lib/playerAnimationSourceIdentity';
import { hasCompleteScheduledSequencePlayback } from '../lib/sequencePlaybackSchedule';
import { usePlayerStoreApi } from '../store/playerStore';
import type {
  PlayerAnimationSource,
  PlayerAssetLoadStatus,
  PlayerResolvedAnimation,
} from '../types';
import { usePlayerSettingsContext } from './playerSettingsContext';
import {
  normalizeBVHForPlayback,
  useBVHAnimation,
  useBVHAnimations,
} from './useBVHAnimation';

export interface UseResolvedPlayerAnimationOptions {
  actorId?: string;
  animation?: PlayerAnimationSource;
}

const INITIAL_RESOLVED_ANIMATION: PlayerResolvedAnimation = {
  kind: 'none',
  sourceUrls: [],
  bvh: null,
  bvhs: [],
  status: 'initial',
  isLoading: false,
  isReady: false,
  error: null,
};
const EMPTY_BVH_URLS: string[] = [];

export function useResolvedPlayerAnimation({
  actorId,
  animation,
}: UseResolvedPlayerAnimationOptions): PlayerResolvedAnimation {
  const store = usePlayerStoreApi();
  const singleAnimation = useBVHAnimation({
    bvh: animation?.type === 'bvh' ? animation.bvh : undefined,
    bvhUrl: animation?.type === 'url' ? animation.url : undefined,
    rotationOffsets:
      animation?.type === 'url' ? animation.rotationOffsets : undefined,
  });
  const sequenceAnimation = useBVHAnimations({
    bvhUrls: animation?.type === 'sequence' ? animation.urls : EMPTY_BVH_URLS,
    reloadKey:
      animation?.type === 'sequence' ? animation.reloadKey : undefined,
    rotationOffsets:
      animation?.type === 'sequence' ? animation.rotationOffsets : undefined,
  });
  const {
    settings: {
      playback: { frameRate: playbackFrameRate },
    },
  } = usePlayerSettingsContext();
  const mergedSequenceBvh = useMemo(() => {
    if (animation?.type !== 'sequence' || sequenceAnimation.bvhs.length === 0) {
      return null;
    }

    if (
      hasCompleteScheduledSequencePlayback(
        animation,
        sequenceAnimation.bvhs.length,
      )
    ) {
      const scheduledClipStartFrames = animation.clipStartFrames!;
      const scheduledTotalDurationFrames = animation.totalDurationFrames!;
      const frameRate =
        animation.timelineFrameRate && animation.timelineFrameRate > 0
          ? animation.timelineFrameRate
          : playbackFrameRate || 24;
      const segments = sequenceAnimation.bvhs.map((bvh, index) => ({
        bvh,
        startTimeSeconds:
          Math.max(0, scheduledClipStartFrames[index] ?? 0) / frameRate,
      }));
      const totalDurationSeconds = scheduledTotalDurationFrames / frameRate;
      return normalizeBVHForPlayback(
        mergeBVHFilesAtTimelineOffsets(segments, totalDurationSeconds),
      );
    }

    return normalizeBVHForPlayback(mergeBVHFiles(sequenceAnimation.bvhs));
  }, [animation, playbackFrameRate, sequenceAnimation.bvhs]);

  const resolvedAnimation = useMemo<PlayerResolvedAnimation>(() => {
    if (!animation) {
      return INITIAL_RESOLVED_ANIMATION;
    }

    if (animation.type === 'sequence') {
      const sequenceSourceMatchesRequest = resolvedSequenceMatchesRequestedSource(
        animation.urls,
        sequenceAnimation.bvhUrls,
      );
      const status: PlayerAssetLoadStatus =
        sequenceAnimation.isLoading || !sequenceSourceMatchesRequest
          ? 'pending'
          : mergedSequenceBvh
            ? 'ready'
            : 'initial';

      return {
        kind: 'sequence',
        sourceUrls: sequenceSourceMatchesRequest
          ? sequenceAnimation.bvhUrls
          : [],
        bvh: sequenceSourceMatchesRequest ? mergedSequenceBvh : null,
        bvhs: sequenceSourceMatchesRequest ? sequenceAnimation.bvhs : [],
        status,
        isLoading: status === 'pending',
        isReady: status === 'ready',
        error: sequenceAnimation.error,
      };
    }

    const singleSourceMatchesRequest =
      animation.type !== 'url' ||
      (Boolean(singleAnimation.bvhUrl) &&
        singleAnimation.bvhUrl === animation.url);
    const status: PlayerAssetLoadStatus =
      singleAnimation.isLoading || !singleSourceMatchesRequest
        ? 'pending'
        : singleAnimation.bvh
          ? 'ready'
          : 'initial';

    return {
      kind:
        animation.type === 'bvh' || animation.type === 'url'
          ? 'single'
          : 'none',
      sourceUrls:
        singleSourceMatchesRequest && singleAnimation.bvhUrl
          ? [singleAnimation.bvhUrl]
          : [],
      bvh: singleSourceMatchesRequest ? singleAnimation.bvh : null,
      bvhs:
        singleSourceMatchesRequest && singleAnimation.bvh
          ? [singleAnimation.bvh]
          : [],
      status,
      isLoading: status === 'pending',
      isReady: status === 'ready',
      error: singleAnimation.error,
    };
  }, [
    animation,
    mergedSequenceBvh,
    sequenceAnimation.error,
    sequenceAnimation.bvhUrls,
    sequenceAnimation.isLoading,
    singleAnimation.bvh,
    singleAnimation.bvhUrl,
    singleAnimation.error,
    singleAnimation.isLoading,
  ]);

  useEffect(() => {
    if (!actorId) {
      return;
    }

    store.setActorAnimationStatus(actorId, resolvedAnimation.status);
    store.setActorAssetError(actorId, resolvedAnimation.error);
    store.setResolvedAnimation(actorId, resolvedAnimation);
  }, [actorId, resolvedAnimation, store]);

  return resolvedAnimation;
}
